/**
 * Random numbers for rule ops.
 *
 * The game draws every economic random number with FWP.true_random(a, b) = Math.floor(random() * (b - a + 1)) + a
 * (FWP.as:21860; random() is as3.random, a uniform [0, 1) source). Rules draw through `RuleRng.int(min, max)`, which
 * applies the same formula to an injected [0, 1) source:
 *  - the server: `rngFromSource(unitRandom(cryptoRandomSource()))` (src/shared/random.ts);
 *  - deterministic simulations and tests: `rngFromSource(seededUnit(seed))`;
 *  - differential tests: `ScriptedRng`, which replays the draws the game made while a fixture was recorded and
 *    checks that the rule asks for the same ranges in the same order.
 */
import type { RandomSource } from '../../shared/random';

/** Integer draws of a rule op, the rules' counterpart of FWP.true_random. */
export interface RuleRng {
  /** FWP.true_random(min, max): an integer in [min, max] (both inclusive for integer bounds). */
  int(min: number, max: number): number;
}

/** A uniform source of doubles in [0, 1). */
export type UnitSource = () => number;

/** One recorded draw: the arguments of true_random and the value it returned. */
export interface RngDraw {
  min: number;
  max: number;
  value: number;
}

/** FWP.true_random's formula over `r` in [0, 1): Math.floor(r * (b - a + 1)) + a, with Number() argument coercion. */
export function trueRandom(r: number, a: number, b: number): number {
  a = Number(a);
  b = Number(b);
  return Math.floor(r * (b - a + 1)) + a;
}

/** A RuleRng drawing from a [0, 1) source with the true_random formula. */
export function rngFromSource(source: UnitSource): RuleRng {
  return { int: (min, max) => trueRandom(source(), min, max) };
}

/** [0, 1) doubles with 53 random bits from a byte source (e.g. cryptoRandomSource() on the server). */
export function unitRandom(src: RandomSource): UnitSource {
  return () => {
    const b = src.bytes(7);
    // 21 high bits + 32 low bits = 53 bits.
    const hi = ((b[0] << 13) | (b[1] << 5) | (b[2] >>> 3)) >>> 0;
    const lo = ((b[3] << 24) | (b[4] << 16) | (b[5] << 8) | b[6]) >>> 0;
    return (hi * 4294967296 + lo) / 9007199254740992;
  };
}

/** Deterministic [0, 1) source (mulberry32, the same generator as the game runtime's default as3.random). */
export function seededUnit(seed: number): UnitSource {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Wraps a RuleRng and records every draw (e.g. to store the draws of a server op next to its result). */
export function recordingRng(inner: RuleRng): { rng: RuleRng; draws: RngDraw[] } {
  const draws: RngDraw[] = [];
  return {
    draws,
    rng: {
      int(min, max) {
        const value = inner.int(min, max);
        draws.push({ min, max, value });
        return value;
      },
    },
  };
}

/** Thrown by ScriptedRng when the rule draws differently from the recorded game. */
export class RngScriptError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RngScriptError';
  }
}

/**
 * Replays recorded draws: the n-th int(min, max) must ask for exactly the n-th recorded (min, max) and gets the
 * recorded value. Asking for more draws than were recorded, or for another range, throws RngScriptError;
 * `assertExhausted()` fails when the rule made fewer draws than the game did.
 */
export class ScriptedRng implements RuleRng {
  private next = 0;
  constructor(private readonly draws: readonly RngDraw[]) {}

  int(min: number, max: number): number {
    const i = this.next;
    const d = this.draws[i];
    if (!d) throw new RngScriptError(`draw #${i + 1} int(${min}, ${max}): the recorded game made only ${this.draws.length} draw(s)`);
    if (d.min !== Number(min) || d.max !== Number(max)) {
      throw new RngScriptError(`draw #${i + 1}: the rule asked for int(${min}, ${max}), the recorded game drew true_random(${d.min}, ${d.max})`);
    }
    this.next++;
    return d.value;
  }

  /** Number of draws consumed so far. */
  get used(): number {
    return this.next;
  }

  assertExhausted(): void {
    if (this.next !== this.draws.length) {
      throw new RngScriptError(`the rule made ${this.next} draw(s), the recorded game made ${this.draws.length}`);
    }
  }
}

/** A RuleRng for ops that must not draw at all: any draw throws. */
export const NO_RANDOM: RuleRng = {
  int(min, max) {
    throw new RngScriptError(`unexpected random draw int(${min}, ${max})`);
  },
};
